// @ts-nocheck
import React, { useCallback, useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import PropTypes from 'prop-types';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';

const getGroupSelectOptionLabel = (option) => option?.groupName;

/**
 * Group Select field component for selecting organization groups
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current selected group name or id
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Array} props.groupList - List of available groups
 * @returns {React.ReactElement} The GroupSelectFieldComponent
 */
const GroupSelectFieldComponent = ({
  value,
  label,
  titleLabel,
  onChange,
  groupList = [],
  attribute,
  isLoadingConstraints = false,
}) => {
  /**
   * Handles group selection changes
   * @function
   * @param {Event} event - The change event
   * @param {Object} group - The selected group
   * @returns {void}
   */
  const isDerived = useMemo(
    () => attribute?.is_derived,
    [attribute?.is_derived]
  );

  const handleChange = useCallback(
    (_event, group) => {
      onChange(group ? group?.id : null);
    },
    [onChange]
  );

  // Find the group object that matches the current value (whether it's a groupName or id)
  const selectedGroup = useMemo(() => {
    if (!value) return null;

    // Check if value is an id (number) or groupName (string)
    const isGroupId = !isNaN(Number(value));

    return groupList.find((group) => {
      if (isGroupId) {
        return group?.id === Number(value);
      } else {
        return group?.groupName === value;
      }
    });
  }, [groupList, value]);

  const renderGroupSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        placeholder={isDerived ? '-' : 'Select group'}
        InputProps={{
          ...params.InputProps,
          placeholder: 'Select group',
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    ),
    [label, titleLabel, isDerived]
  );

  // When we do not change the group, then by default value is groupName as we are getting groupName from backend, but when we change the group, we send the id to the backend.
  // So we need to check if the value is an id or groupName and then set the value accordingly, so when user directly try to save the group without changing it, we send the id to the backend.
  useEffect(() => {
    if (value) {
      // Check if value is an id (number) or groupName (string)
      const isGroupId = !isNaN(Number(value));
      if (isGroupId) {
        onChange(value);
      } else {
        onChange(selectedGroup?.id);
      }
    }
  }, []);

  return (
    <span
      title={
        isDerived
          ? 'This field is disabled because it is derived'
          : `${attribute?.label}`
      }
    >
      <Autocomplete
        fullWidth
        disabled={isDerived || isLoadingConstraints}
        value={selectedGroup || null}
        options={groupList}
        popupIcon={<DropdownArrow />}
        getOptionLabel={getGroupSelectOptionLabel}
        renderInput={renderGroupSelectTextField}
        onChange={handleChange}
        style={{ pointerEvents: isDerived ? 'none' : 'auto' }}
      />
    </span>
  );
};

GroupSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    description: PropTypes.string,
    is_derived: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    // Add other attribute properties as needed
  }).isRequired,
  groupList: PropTypes.arrayOf(
    PropTypes.shape({
      groupName: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      // Add other group properties as needed
    })
  ),
  isLoadingConstraints: PropTypes.bool,
};

export default GroupSelectFieldComponent;
